import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Flag, Star, Sparkles, Gift, X, Heart, Flower2, Crown, Music, Moon, Zap,
  Target, Camera, TreePine, Globe, Leaf, Camera as CameraIcon, Users, 
  TreePine as TreeIcon, Globe as GlobeIcon, Leaf as LeafIcon, Target as TargetIcon,
  ArrowRight, ArrowLeft, Shield, Flame, Sun, Cloud, Droplets, Palette,
  TrendingUp, BarChart3, Smartphone, Monitor, Wifi, Database, Code, Zap as Lightning,
  Wind, Feather, Wheat, Coffee, Home, Mountain, Sunrise, Sunset,
  Clock, Calendar, PartyPopper, Bell, Award, Trophy, Rocket, Zap as LightningBolt
} from 'lucide-react';

const SpecialDayEffects = () => {
  const location = useLocation();
  const [specialDay, setSpecialDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bannerVisible, setBannerVisible] = useState(true);
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const fireworksRef = useRef([]);
  const animationRef = useRef(null);

  // Export banner component for use in Layout
  const SpecialDayBanner = () => {
    // Get the current state from the main component
    const currentBannerVisible = window.SpecialDayBannerVisible !== false;
    const currentSpecialDay = window.SpecialDayData;
    
    if (!currentBannerVisible || !currentSpecialDay || !currentSpecialDay.is_active || !currentSpecialDay.effects_enabled) {
      return null;
    }

    const handleCloseBanner = () => {
      if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🔘 Close button clicked!');
      // Call the close function from the main component
      if (window.closeSpecialDayBanner) {
        window.closeSpecialDayBanner();
        if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('✅ Banner closed successfully');
      } else {
        if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('❌ Close function not found');
      }
    };

    // Determine banner styling based on special day
    const getBannerStyle = () => {
      const title = currentSpecialDay.title.toLowerCase();
      
      if (title.includes('vinayaka') || title.includes('ganesh')) {
        return {
          background: 'linear-gradient(90deg, #FF6B35, #F7931E, #FFD700, #FF6B35)',
          icon: <Crown className="w-4 h-4" />,
          textColor: 'text-yellow-800'
        };
      } else if (title.includes('krishna') || title.includes('janmashtami')) {
        return {
          background: 'linear-gradient(90deg, #1E3A8A, #3B82F6, #8B5CF6, #1E3A8A)',
          icon: <Music className="w-4 h-4" />,
          textColor: 'text-blue-100'
        };
      } else if (title.includes('dussehra')) {
        return {
          background: 'linear-gradient(90deg, #DC2626, #F59E0B, #DC2626)',
          icon: <Target className="w-4 h-4" />,
          textColor: 'text-red-100'
        };
      } else if (title.includes('diwali')) {
        return {
          background: 'linear-gradient(90deg, #F59E0B, #FCD34D, #F59E0B)',
          icon: <Sparkles className="w-4 h-4" />,
          textColor: 'text-yellow-800'
        };
      } else if (title.includes('christmas')) {
        return {
          background: 'linear-gradient(90deg, #DC2626, #F59E0B, #FFFFFF, #3B82F6, #8B5CF6, #DC2626)',
          icon: <TreePine className="w-4 h-4" />,
          textColor: 'text-white'
        };
      } else if (title.includes('photography')) {
        return {
          background: 'linear-gradient(90deg, #1F2937, #6B7280, #1F2937)',
          icon: <Camera className="w-4 h-4" />,
          textColor: 'text-gray-100'
        };
      } else if (title.includes('rakhi')) {
        return {
          background: 'linear-gradient(90deg, #EC4899, #F472B6, #EC4899)',
          icon: <Heart className="w-4 h-4" />,
          textColor: 'text-pink-100'
        };
      } else if (title.includes('friendship')) {
        return {
          background: 'linear-gradient(90deg, #3B82F6, #60A5FA, #3B82F6)',
          icon: <Users className="w-4 h-4" />,
          textColor: 'text-blue-100'
        };
      } else if (title.includes('earth day')) {
        return {
          background: 'linear-gradient(90deg, #059669, #10B981, #059669)',
          icon: <Globe className="w-4 h-4" />,
          textColor: 'text-green-100'
        };
      } else if (title.includes('environment')) {
        return {
          background: 'linear-gradient(90deg, #059669, #34D399, #059669)',
          icon: <Leaf className="w-4 h-4" />,
          textColor: 'text-green-100'
        };
      } else if (title.includes('valentine')) {
        return {
          background: 'linear-gradient(90deg, #EC4899, #F472B6, #EC4899)',
          icon: <Heart className="w-4 h-4" />,
          textColor: 'text-pink-100'
        };
      } else if (title.includes('ram navami')) {
        return {
          background: 'linear-gradient(90deg, #1E40AF, #3B82F6, #1E40AF)',
          icon: <Target className="w-4 h-4" />,
          textColor: 'text-blue-100'
        };
      } else if (title.includes('bakrid') || title.includes('ramzan')) {
        return {
          background: 'linear-gradient(90deg, #059669, #10B981, #059669)',
          icon: <Moon className="w-4 h-4" />,
          textColor: 'text-green-100'
        };
      } else if (title.includes('republic')) {
        return {
          background: 'linear-gradient(90deg, #FF9933, #FFFFFF, #138808)',
          icon: <Flag className="w-4 h-4" />,
          textColor: 'text-orange-800'
        };
      } else if (title.includes('shivratri')) {
        return {
          background: 'linear-gradient(90deg, #7C3AED, #A78BFA, #7C3AED)',
          icon: <Zap className="w-4 h-4" />,
          textColor: 'text-purple-100'
        };
      } else if (title.includes('holi')) {
        return {
          background: 'linear-gradient(90deg, #EC4899, #F59E0B, #10B981, #3B82F6, #EC4899)',
          icon: <Palette className="w-4 h-4" />,
          textColor: 'text-white'
        };
      } else if (title.includes('digital marketing')) {
        return {
          background: 'linear-gradient(90deg, #1F2937, #6B7280, #1F2937)',
          icon: <TrendingUp className="w-4 h-4" />,
          textColor: 'text-gray-100'
        };
      } else if (title.includes('sankranthi')) {
        return {
          background: 'linear-gradient(90deg, #F59E0B, #FCD34D, #10B981, #F59E0B)',
          icon: <Sun className="w-4 h-4" />,
          textColor: 'text-yellow-800'
        };
      } else if (title.includes('new year')) {
        return {
          background: 'linear-gradient(90deg, #1E40AF, #3B82F6, #8B5CF6, #EC4899, #F59E0B, #1E40AF)',
          icon: <Clock className="w-4 h-4" />,
          textColor: 'text-white'
        };
      } else {
        // Default Independence Day styling
        return {
          background: 'linear-gradient(90deg, #FF9933, #FFFFFF, #138808)',
          icon: <Flag className="w-4 h-4" />,
          textColor: 'text-orange-800'
        };
      }
    };

    const bannerStyle = getBannerStyle();

    return (
      <div 
        className="special-day-banner sticky top-0 left-0 right-0 text-center py-2 z-20 shadow-lg w-full"
        style={{ background: bannerStyle.background }}
      >
        <div className="flex items-center justify-between px-4">
          <div className="flex items-center justify-center gap-2 text-sm font-medium flex-1">
            {bannerStyle.icon}
            <span className={bannerStyle.textColor}>
              {currentSpecialDay.title} - {currentSpecialDay.description}
            </span>
            {currentSpecialDay.coupon_code && (
              <div className="flex items-center gap-1 ml-4">
                <Gift className="w-4 h-4 text-green-600" />
                <span className="text-green-700 font-bold">
                  Use code: {currentSpecialDay.coupon_code} for {currentSpecialDay.coupon_discount}% off!
                </span>
              </div>
            )}
          </div>
          <button
            onClick={handleCloseBanner}
            className="text-gray-600 hover:text-gray-800 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
            title="Close banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // Make banner component and state available globally
  useEffect(() => {
    window.SpecialDayBanner = SpecialDayBanner;
    window.SpecialDayBannerVisible = bannerVisible;
    window.SpecialDayData = specialDay;
    window.closeSpecialDayBanner = () => {
      setBannerVisible(false);
      window.SpecialDayBannerVisible = false;
    };
    
    if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🌐 Global banner state updated:', bannerVisible);
  }, [bannerVisible, specialDay]);

  useEffect(() => {
    fetchActiveSpecialDay();
    // Always start with banner visible - no persistence
    setBannerVisible(true);
    
    // Listen for refresh events from admin panel
    const handleRefresh = () => {
      if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🔄 Refreshing special day effects...');
      fetchActiveSpecialDay();
    };
    
    window.addEventListener('specialDayEffectsRefresh', handleRefresh);
    
    return () => {
      window.removeEventListener('specialDayEffectsRefresh', handleRefresh);
    };
  }, []);

  // Handle body class for banner visibility
  useEffect(() => {
    if (specialDay && specialDay.is_active && specialDay.effects_enabled && bannerVisible) {
      document.body.classList.add('special-day-banner-visible');
    } else {
      document.body.classList.remove('special-day-banner-visible');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('special-day-banner-visible');
    };
  }, [bannerVisible, specialDay]);

  useEffect(() => {
    if (specialDay && specialDay.is_active && specialDay.effects_enabled) {
      initializeEffects();
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    } else {
      // Clean up effects when special day is inactive or effects are disabled
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      
      // Clear canvas
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
      
      // Remove custom CSS and JS
      applyCustomCSS('');
      applyCustomJS('');
    }
  }, [specialDay]);

  const fetchActiveSpecialDay = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/special-days/active`);
      const data = await response.json();
      
      if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🔄 Fetched special day data:', data);
      
      if (data.success && data.data) {
        if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('✅ Setting special day:', {
          id: data.data.id,
          is_active: data.data.is_active,
          effects_enabled: data.data.effects_enabled,
          title: data.data.title
        });
        setSpecialDay(data.data);
        
        // Reset banner visibility for new special days
        const lastSpecialDayId = localStorage.getItem('lastSpecialDayId');
        if (lastSpecialDayId !== data.data.id.toString()) {
          setBannerVisible(true);
          localStorage.setItem('lastSpecialDayId', data.data.id.toString());
        }
        
        // Apply custom CSS (or remove if empty)
        applyCustomCSS(data.data.custom_css || '');
        
        // Apply custom JavaScript (or remove if empty)
        applyCustomJS(data.data.custom_js || '');
      } else {
        if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('❌ No active special day found - clearing effects');
        setSpecialDay(null);
        setBannerVisible(false); // Reset banner visibility when no active special day
        // Remove custom CSS and JS when no active special day
        applyCustomCSS('');
        applyCustomJS('');
      }
    } catch (error) {
      console.error('Error fetching active special day:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyCustomCSS = (css) => {
    const styleId = 'special-day-custom-css';
    let styleElement = document.getElementById(styleId);
    
    if (!css || css.trim() === '') {
      // Remove the style element if CSS is empty
      if (styleElement) {
        styleElement.remove();
      }
      return;
    }
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    
    styleElement.textContent = css;
  };

  const applyCustomJS = (js) => {
    const scriptId = 'special-day-custom-js';
    let existingScript = document.getElementById(scriptId);
    
    // Remove existing script if any
    if (existingScript) {
      existingScript.remove();
    }
    
    if (!js || js.trim() === '') {
      return;
    }
    
    try {
      // Create a safe execution context for custom JS
      const script = document.createElement('script');
      script.id = scriptId;
      script.textContent = `
        (function() {
          ${js}
        })();
      `;
      document.head.appendChild(script);
    } catch (error) {
      console.error('Error applying custom JavaScript:', error);
    }
  };

  const handleCloseBanner = () => {
    setBannerVisible(false);
    // Don't store in any storage - let it reset on page reload
  };

  const initializeEffects = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      // Ensure canvas doesn't overflow viewport
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.maxWidth = '100vw';
      canvas.style.maxHeight = '100vh';
      canvas.style.overflow = 'hidden';
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize particles
    if (backgroundEffects?.particles) {
      initializeParticles(canvas, ctx);
    }

    // Initialize fireworks
    if (backgroundEffects?.fireworks) {
      initializeFireworks(canvas, ctx);
    }

    // Start animation loop
    animate(canvas, ctx);
  };

  const initializeParticles = (canvas, ctx) => {
    const particleCount = 50;
    particlesRef.current = [];

    for (let i = 0; i < particleCount; i++) {
      particlesRef.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        size: Math.random() * 3 + 1,
        color: getRandomFlagColor(),
        opacity: Math.random() * 0.5 + 0.3
      });
    }
  };

  const initializeFireworks = (canvas, ctx) => {
    fireworksRef.current = [];
  };

  const getRandomFlagColor = () => {
    const colors = ['#FF9933', '#FFFFFF', '#138808']; // Saffron, White, Green
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const animate = (canvas, ctx) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Update and draw particles
    if (backgroundEffects?.particles) {
      updateParticles(canvas, ctx);
    }

    // Update and draw fireworks
    if (backgroundEffects?.fireworks) {
      updateFireworks(canvas, ctx);
    }

    // Add new fireworks randomly
    if (backgroundEffects?.fireworks && Math.random() < 0.02) {
      addFirework(canvas);
    }

    animationRef.current = requestAnimationFrame(() => animate(canvas, ctx));
  };

  const updateParticles = (canvas, ctx) => {
    particlesRef.current.forEach((particle, index) => {
      // Update position
      particle.x += particle.vx;
      particle.y += particle.vy;

      // Bounce off edges with proper boundary checking
      if (particle.x <= 0) {
        particle.x = 0;
        particle.vx = Math.abs(particle.vx);
      }
      if (particle.x >= canvas.width) {
        particle.x = canvas.width;
        particle.vx = -Math.abs(particle.vx);
      }
      if (particle.y <= 0) {
        particle.y = 0;
        particle.vy = Math.abs(particle.vy);
      }
      if (particle.y >= canvas.height) {
        particle.y = canvas.height;
        particle.vy = -Math.abs(particle.vy);
      }

      // Draw particle
      ctx.save();
      ctx.globalAlpha = particle.opacity;
      ctx.fillStyle = particle.color;
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });
  };

  const addFirework = (canvas) => {
    fireworksRef.current.push({
      x: Math.random() * (canvas.width - 100) + 50, // Keep away from edges
      y: canvas.height,
      targetY: Math.random() * canvas.height * 0.6,
      speed: Math.random() * 3 + 2,
      particles: [],
      exploded: false,
      color: getRandomFlagColor()
    });
  };

  const updateFireworks = (canvas, ctx) => {
    fireworksRef.current.forEach((firework, index) => {
      if (!firework.exploded) {
        // Move firework up
        firework.y -= firework.speed;

        // Draw firework trail
        ctx.save();
        ctx.strokeStyle = firework.color;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.8;
        ctx.beginPath();
        ctx.moveTo(firework.x, firework.y);
        ctx.lineTo(firework.x, firework.y + 20);
        ctx.stroke();
        ctx.restore();

        // Check if firework reached target
        if (firework.y <= firework.targetY) {
          firework.exploded = true;
          // Create explosion particles
          for (let i = 0; i < 30; i++) {
            firework.particles.push({
              x: firework.x,
              y: firework.y,
              vx: (Math.random() - 0.5) * 8,
              vy: (Math.random() - 0.5) * 8,
              life: 1,
              decay: Math.random() * 0.02 + 0.01,
              color: firework.color
            });
          }
        }
      } else {
        // Update explosion particles
        firework.particles.forEach((particle, particleIndex) => {
          particle.x += particle.vx;
          particle.y += particle.vy;
          particle.vy += 0.1; // Gravity
          particle.life -= particle.decay;

          // Draw particle
          if (particle.life > 0) {
            ctx.save();
            ctx.globalAlpha = particle.life;
            ctx.fillStyle = particle.color;
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, 2, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          } else {
            firework.particles.splice(particleIndex, 1);
          }
        });

        // Remove firework if all particles are gone
        if (firework.particles.length === 0) {
          fireworksRef.current.splice(index, 1);
        }
      }
    });
  };

  // Parse JSON settings with useMemo to prevent unnecessary re-parsing
  const backgroundEffects = useMemo(() => {
    if (!specialDay?.background_effects) return null;
    return typeof specialDay.background_effects === 'string' 
      ? JSON.parse(specialDay.background_effects) 
      : specialDay.background_effects;
  }, [specialDay?.background_effects]);
  
  const animationSettings = useMemo(() => {
    if (!specialDay?.animation_settings) return null;
    return typeof specialDay.animation_settings === 'string' 
      ? JSON.parse(specialDay.animation_settings) 
      : specialDay.animation_settings;
  }, [specialDay?.animation_settings]);

  // Don't render on admin pages
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  if (loading) return null;
  if (!specialDay || !specialDay.is_active || !specialDay.effects_enabled) return null;

  if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🎉 Special Day Effects Active:', {
    backgroundEffects,
    animationSettings,
    bannerVisible
  });
  
  if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🎨 Rendering effects:', {
    canvas: !!canvasRef.current,
    particles: backgroundEffects?.particles,
    fireworks: backgroundEffects?.fireworks,
    flagColors: backgroundEffects?.flag_colors,
    headerAnimation: animationSettings?.header_animation,
    buttonEffects: animationSettings?.button_effects,
    scrollEffects: animationSettings?.scroll_effects
  });

  // Debug flag colors specifically
  if (backgroundEffects?.flag_colors) {
    if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🇮🇳 Flag colors effect is ENABLED - should show card animations!');
  } else {
    if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('❌ Flag colors effect is DISABLED');
  }

  return (
    <div className="special-day-effects-container">
      {/* Prevent overflow from special day effects */}
      <style>
        {`
          body {
            overflow-x: hidden !important;
            max-width: 100vw !important;
          }
          
          .special-day-effects-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 5;
            overflow: hidden;
          }
        `}
      </style>

      {/* Background Canvas for Effects */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none"
        style={{ 
          zIndex: 5,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden'
        }}
      />



      {/* Floating Elements - Multiple positions throughout the page */}
      {animationSettings?.header_animation && (
        <>
          {/* Determine floating elements based on special day */}
          {(() => {
            const title = specialDay.title.toLowerCase();
            const isVinayakaChavithi = title.includes('vinayaka') || title.includes('ganesh');
            const isKrishnashtami = title.includes('krishna') || title.includes('janmashtami');
            const isDussehra = title.includes('dussehra');
            const isDiwali = title.includes('diwali');
            const isChristmas = title.includes('christmas');
            const isPhotography = title.includes('photography');
            const isRakhi = title.includes('rakhi');
            const isFriendship = title.includes('friendship');
            const isEarthDay = title.includes('earth day');
            const isEnvironment = title.includes('environment');
            const isValentine = title.includes('valentine');
            const isRamNavami = title.includes('ram navami');
            const isBakrid = title.includes('bakrid');
            const isRamzan = title.includes('ramzan');
            const isRepublic = title.includes('republic');
            const isShivratri = title.includes('shivratri');
            const isHoli = title.includes('holi');
            const isDigitalMarketing = title.includes('digital marketing');
            const isSankranthi = title.includes('sankranthi');
            const isNewYear = title.includes('new year');
            
            if (isDussehra) {
              // Dussehra floating elements
              return (
                <>
                  {/* Top left - Target (Victory) */}
                  <div className="fixed top-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce">
                      <Target className="w-6 h-6 text-red-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Top right - Arrow (Victory) */}
                  <div className="fixed top-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse">
                      <ArrowRight className="w-6 h-6 text-orange-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle left - Shield (Protection) */}
                  <div className="fixed top-1/2 left-8 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '1s' }}>
                      <Shield className="w-5 h-5 text-red-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle right - Flame (Victory) */}
                  <div className="fixed top-1/2 right-8 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '2s' }}>
                      <Flame className="w-5 h-5 text-orange-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom left - Star */}
                  <div className="fixed bottom-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '0.5s' }}>
                      <Star className="w-4 h-4 text-red-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom right - Sparkles */}
                  <div className="fixed bottom-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '1.5s' }}>
                      <Sparkles className="w-4 h-4 text-orange-500 fill-current" />
                    </div>
                  </div>
                </>
              );
            } else if (isDiwali) {
              // Diwali floating elements
              return (
                <>
                  {/* Top left - Sparkles (Lights) */}
                  <div className="fixed top-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce">
                      <Sparkles className="w-6 h-6 text-yellow-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Top right - Star (Light) */}
                  <div className="fixed top-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse">
                      <Star className="w-6 h-6 text-orange-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle left - Gift */}
                  <div className="fixed top-1/2 left-8 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '1s' }}>
                      <Gift className="w-5 h-5 text-yellow-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle right - Heart */}
                  <div className="fixed top-1/2 right-8 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '2s' }}>
                      <Heart className="w-5 h-5 text-orange-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom left - Flower */}
                  <div className="fixed bottom-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '0.5s' }}>
                      <Flower2 className="w-4 h-4 text-yellow-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom right - Zap (Light) */}
                  <div className="fixed bottom-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '1.5s' }}>
                      <Zap className="w-4 h-4 text-orange-500 fill-current" />
                    </div>
                  </div>
                </>
              );
            } else if (isChristmas) {
              // Christmas floating elements
              return (
                <>
                  {/* Top left - Tree */}
                  <div className="fixed top-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce">
                      <TreePine className="w-6 h-6 text-green-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Top right - Star */}
                  <div className="fixed top-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse">
                      <Star className="w-6 h-6 text-yellow-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle left - Gift */}
                  <div className="fixed top-1/2 left-8 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '1s' }}>
                      <Gift className="w-5 h-5 text-red-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle right - Heart */}
                  <div className="fixed top-1/2 right-8 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '2s' }}>
                      <Heart className="w-5 h-5 text-red-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom left - Snow */}
                  <div className="fixed bottom-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '0.5s' }}>
                      <Cloud className="w-4 h-4 text-blue-300 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom right - Sparkles */}
                  <div className="fixed bottom-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '1.5s' }}>
                      <Sparkles className="w-4 h-4 text-white fill-current" />
                    </div>
                  </div>
                </>
              );
            } else if (isPhotography) {
              // Photography Day floating elements
              return (
                <>
                  {/* Top left - Camera */}
                  <div className="fixed top-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce">
                      <Camera className="w-6 h-6 text-gray-600 fill-current" />
                    </div>
                  </div>
                  
                  {/* Top right - Target (Focus) */}
                  <div className="fixed top-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse">
                      <Target className="w-6 h-6 text-gray-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle left - Star */}
                  <div className="fixed top-1/2 left-8 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '1s' }}>
                      <Star className="w-5 h-5 text-gray-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle right - Sparkles */}
                  <div className="fixed top-1/2 right-8 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '2s' }}>
                      <Sparkles className="w-5 h-5 text-gray-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom left - Zap (Flash) */}
                  <div className="fixed bottom-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '0.5s' }}>
                      <Zap className="w-4 h-4 text-yellow-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom right - Heart */}
                  <div className="fixed bottom-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '1.5s' }}>
                      <Heart className="w-4 h-4 text-red-400 fill-current" />
                    </div>
                  </div>
                </>
              );
            } else if (isKrishnashtami) {
              // Vinayaka Chavithi floating elements
              return (
                <>
                  {/* Top left - Lotus */}
                  <div className="fixed top-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce">
                      <Flower2 className="w-6 h-6 text-pink-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Top right - Crown */}
                  <div className="fixed top-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse">
                      <Crown className="w-6 h-6 text-yellow-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle left - Heart */}
                  <div className="fixed top-1/2 left-8 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '1s' }}>
                      <Heart className="w-5 h-5 text-red-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle right - Star */}
                  <div className="fixed top-1/2 right-8 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '2s' }}>
                      <Star className="w-5 h-5 text-yellow-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom left - Sparkles */}
                  <div className="fixed bottom-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '0.5s' }}>
                      <Sparkles className="w-4 h-4 text-orange-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom right - Flower */}
                  <div className="fixed bottom-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '1.5s' }}>
                      <Flower2 className="w-4 h-4 text-pink-500 fill-current" />
                    </div>
                  </div>
                </>
              );
            } else if (isSankranthi) {
              // Sankranthi floating elements (combines Sankranthi, Kanuma, and Bhogi)
              return (
                <>
                  {/* Top left - Sun (Sankranthi) */}
                  <div className="fixed top-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce">
                      <Sun className="w-6 h-6 text-yellow-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Top right - Wind (Kite flying) */}
                  <div className="fixed top-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse">
                      <Wind className="w-6 h-6 text-blue-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle left - Wheat (Harvest) */}
                  <div className="fixed top-1/2 left-8 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '1s' }}>
                      <Wheat className="w-5 h-5 text-yellow-600 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle right - Flame (Bhogi bonfire) */}
                  <div className="fixed top-1/2 right-8 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '2s' }}>
                      <Flame className="w-5 h-5 text-orange-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom left - Home (Kanuma) */}
                  <div className="fixed bottom-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '0.5s' }}>
                      <Home className="w-4 h-4 text-green-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom right - Feather (Kite) */}
                  <div className="fixed bottom-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '1.5s' }}>
                      <Feather className="w-4 h-4 text-red-400 fill-current" />
                    </div>
                  </div>
                </>
              );
            } else if (isNewYear) {
              // New Year floating elements
              return (
                <>
                  {/* Top left - Clock (Countdown) */}
                  <div className="fixed top-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce">
                      <Clock className="w-6 h-6 text-blue-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Top right - Party Popper */}
                  <div className="fixed top-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse">
                      <PartyPopper className="w-6 h-6 text-pink-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle left - Star */}
                  <div className="fixed top-1/2 left-8 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '1s' }}>
                      <Star className="w-5 h-5 text-yellow-400 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle right - Bell */}
                  <div className="fixed top-1/2 right-8 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '2s' }}>
                      <Bell className="w-5 h-5 text-purple-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom left - Trophy */}
                  <div className="fixed bottom-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '0.5s' }}>
                      <Trophy className="w-4 h-4 text-orange-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom right - Rocket */}
                  <div className="fixed bottom-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '1.5s' }}>
                      <Rocket className="w-4 h-4 text-red-500 fill-current" />
                    </div>
                  </div>
                </>
              );
            } else {
              // Default Independence Day floating elements
              return (
                <>
                  {/* Top left */}
                  <div className="fixed top-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce">
                      <Star className="w-6 h-6 text-orange-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Top right */}
                  <div className="fixed top-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse">
                      <Sparkles className="w-6 h-6 text-green-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle left */}
                  <div className="fixed top-1/2 left-8 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '1s' }}>
                      <Star className="w-5 h-5 text-green-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Middle right */}
                  <div className="fixed top-1/2 right-8 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '2s' }}>
                      <Sparkles className="w-5 h-5 text-orange-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom left */}
                  <div className="fixed bottom-20 left-4 z-20 pointer-events-none">
                    <div className="animate-bounce" style={{ animationDelay: '0.5s' }}>
                      <Star className="w-4 h-4 text-green-500 fill-current" />
                    </div>
                  </div>
                  
                  {/* Bottom right */}
                  <div className="fixed bottom-32 right-4 z-20 pointer-events-none">
                    <div className="animate-pulse" style={{ animationDelay: '1.5s' }}>
                      <Sparkles className="w-4 h-4 text-orange-500 fill-current" />
                    </div>
                  </div>
                </>
              );
            }
          })()}
        </>
      )}

      {/* Flag Colors Overlay for Elements */}
      {backgroundEffects?.flag_colors && (() => {
        if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🎨 Applying flag colors CSS with card animations!');
        return true;
      })() && (
        <style>
          {`
            /* Apply flag colors to buttons throughout the website */
            button:not(.admin-button):not(.dashboard-button) {
              background: linear-gradient(45deg, #FF9933, #FFFFFF, #138808) !important;
              background-size: 200% 200% !important;
              animation: flagWave 3s ease-in-out infinite !important;
              color: #000 !important;
              border: none !important;
            }
            
            /* Apply to primary buttons and CTAs */
            .btn-primary, .cta-button, .primary-button {
              background: linear-gradient(45deg, #FF9933, #FFFFFF, #138808) !important;
              background-size: 200% 200% !important;
              animation: flagWave 3s ease-in-out infinite !important;
            }
            
            @keyframes flagWave {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
            
            /* Apply flag colors to headings */
            h1, h2, h3, h4, h5, h6 {
              background: linear-gradient(45deg, #FF9933, #138808) !important;
              -webkit-background-clip: text !important;
              -webkit-text-fill-color: transparent !important;
              background-clip: text !important;
            }
            
            /* Exclude specific elements from gradient effects */
            .hero-title span:not(.text-gradient),
            .hero-title .text-gray-600,
            .hero-title .text-gray-400,
            .hero-title .dark\\:text-gray-600,
            .hero-title .dark\\:text-gray-400 {
              -webkit-text-fill-color: initial !important;
              background: none !important;
              color: inherit !important;
            }
            
            /* Apply to navigation links */
            nav a, .nav-link {
              color: #FF9933 !important;
              transition: color 0.3s ease !important;
            }
            
            nav a:hover, .nav-link:hover {
              color: #138808 !important;
            }
            
            .special-day-button {
              background: linear-gradient(45deg, #FF9933, #FFFFFF, #138808);
              background-size: 200% 200%;
              animation: flagWave 3s ease-in-out infinite;
            }
            
            .special-day-text {
              background: linear-gradient(45deg, #FF9933, #138808);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-clip: text;
            }
            
            .special-day-border {
              border: 2px solid;
              border-image: linear-gradient(45deg, #FF9933, #FFFFFF, #138808) 1;
            }
            
            /* Scrolling flag-gradient animation for all cards */
            .card, .service-card, .blog-card, .product-card, .project-card, .contact-card {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            /* More specific selectors for better targeting */
            div[class*="card"], div[class*="Card"] {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            .card::before, .service-card::before, .blog-card::before, .product-card::before, .project-card::before, .contact-card::before,
            div[class*="card"]::before, div[class*="Card"]::before {
              content: '' !important;
              position: absolute !important;
              top: -8px !important;
              left: -8px !important;
              right: -8px !important;
              bottom: -8px !important;
              background: linear-gradient(90deg, 
                #FF9933 0%, 
                #FFFFFF 25%, 
                #138808 50%, 
                #FFFFFF 75%, 
                #FF9933 100%
              ) !important;
              background-size: 200% 100% !important;
              animation: flagScroll 2s linear infinite !important;
              z-index: 1 !important;
              pointer-events: none !important;
              border-radius: 16px !important;
            }
            
            .card::after, .service-card::after, .blog-card::after, .product-card::after, .project-card::after, .contact-card::after,
            div[class*="card"]::after, div[class*="Card"]::after {
              content: '' !important;
              position: absolute !important;
              top: 6px !important;
              left: 6px !important;
              right: 6px !important;
              bottom: 6px !important;
              background: inherit !important;
              border-radius: inherit !important;
              z-index: 2 !important;
            }
            
            @keyframes flagScroll {
              0% { background-position: 0% 50%; }
              100% { background-position: 200% 50%; }
            }
            
            /* Ensure card content stays above the animation */
            .card > *, .service-card > *, .blog-card > *, .product-card > *, .project-card > *, .contact-card > *,
            div[class*="card"] > *, div[class*="Card"] > * {
              position: relative !important;
              z-index: 3 !important;
            }
          `}
        </style>
      )}

      {/* Divine Colors for Vinayaka Chavithi */}
      {backgroundEffects?.divine_colors && (
        <style>
          {`
            /* Apply divine colors to buttons throughout the website */
            button:not(.admin-button):not(.dashboard-button) {
              background: linear-gradient(45deg, #FF6B35, #F7931E, #FFD700, #FF6B35) !important;
              background-size: 200% 200% !important;
              animation: divineWave 4s ease-in-out infinite !important;
              color: #000 !important;
              border: none !important;
            }
            
            /* Apply to primary buttons and CTAs */
            .btn-primary, .cta-button, .primary-button {
              background: linear-gradient(45deg, #FF6B35, #F7931E, #FFD700, #FF6B35) !important;
              background-size: 200% 200% !important;
              animation: divineWave 4s ease-in-out infinite !important;
            }
            
            @keyframes divineWave {
              0%, 100% { background-position: 0% 50%; }
              25% { background-position: 100% 50%; }
              50% { background-position: 100% 100%; }
              75% { background-position: 0% 100%; }
            }
            
            /* Apply divine colors to headings */
            h1, h2, h3, h4, h5, h6 {
              background: linear-gradient(45deg, #FF6B35, #F7931E, #FFD700) !important;
              -webkit-background-clip: text !important;
              -webkit-text-fill-color: transparent !important;
              background-clip: text !important;
            }
            
            /* Exclude specific elements from gradient effects */
            .hero-title span:not(.text-gradient),
            .hero-title .text-gray-600,
            .hero-title .text-gray-400,
            .hero-title .dark\\:text-gray-600,
            .hero-title .dark\\:text-gray-400 {
              -webkit-text-fill-color: initial !important;
              background: none !important;
              color: inherit !important;
            }
            
            /* Apply to navigation links */
            nav a, .nav-link {
              color: #FF6B35 !important;
              transition: color 0.3s ease !important;
            }
            
            nav a:hover, .nav-link:hover {
              color: #FFD700 !important;
            }
            
            .special-day-button {
              background: linear-gradient(45deg, #FF6B35, #F7931E, #FFD700, #FF6B35);
              background-size: 200% 200%;
              animation: divineWave 4s ease-in-out infinite;
            }
            
            .special-day-text {
              background: linear-gradient(45deg, #FF6B35, #F7931E, #FFD700);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-clip: text;
            }
            
            .special-day-border {
              border: 2px solid;
              border-image: linear-gradient(45deg, #FF6B35, #F7931E, #FFD700) 1;
            }
            
            /* Scrolling divine gradient animation for all cards */
            .card, .service-card, .blog-card, .product-card, .project-card, .contact-card {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            /* More specific selectors for better targeting */
            div[class*="card"], div[class*="Card"] {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            .card::before, .service-card::before, .blog-card::before, .product-card::before, .project-card::before, .contact-card::before,
            div[class*="card"]::before, div[class*="Card"]::before {
              content: '' !important;
              position: absolute !important;
              top: -8px !important;
              left: -8px !important;
              right: -8px !important;
              bottom: -8px !important;
              background: linear-gradient(90deg, 
                #FF6B35 0%, 
                #F7931E 25%, 
                #FFD700 50%, 
                #F7931E 75%, 
                #FF6B35 100%
              ) !important;
              background-size: 200% 100% !important;
              animation: divineScroll 2s linear infinite !important;
              z-index: 1 !important;
              pointer-events: none !important;
              border-radius: 16px !important;
            }
            
            .card::after, .service-card::after, .blog-card::after, .product-card::after, .project-card::after, .contact-card::after,
            div[class*="card"]::after, div[class*="Card"]::after {
              content: '' !important;
              position: absolute !important;
              top: 6px !important;
              left: 6px !important;
              right: 6px !important;
              bottom: 6px !important;
              background: inherit !important;
              border-radius: inherit !important;
              z-index: 2 !important;
            }
            
            @keyframes divineScroll {
              0% { background-position: 0% 50%; }
              100% { background-position: 200% 50%; }
            }
            
            /* Ensure card content stays above the animation */
            .card > *, .service-card > *, .blog-card > *, .product-card > *, .project-card > *, .contact-card > *,
            div[class*="card"] > *, div[class*="Card"] > * {
              position: relative !important;
              z-index: 3 !important;
            }
          `}
        </style>
      )}

      {/* Krishna Colors for Krishnashtami */}
      {backgroundEffects?.krishna_colors && (
        <style>
          {`
            /* Apply Krishna colors to buttons throughout the website */
            button:not(.admin-button):not(.dashboard-button) {
              background: linear-gradient(45deg, #1E3A8A, #3B82F6, #8B5CF6, #1E3A8A) !important;
              background-size: 200% 200% !important;
              animation: krishnaWave 5s ease-in-out infinite !important;
              color: #fff !important;
              border: none !important;
            }
            
            /* Apply to primary buttons and CTAs */
            .btn-primary, .cta-button, .primary-button {
              background: linear-gradient(45deg, #1E3A8A, #3B82F6, #8B5CF6, #1E3A8A) !important;
              background-size: 200% 200% !important;
              animation: krishnaWave 5s ease-in-out infinite !important;
            }
            
            @keyframes krishnaWave {
              0%, 100% { background-position: 0% 50%; }
              20% { background-position: 100% 50%; }
              40% { background-position: 100% 100%; }
              60% { background-position: 0% 100%; }
              80% { background-position: 0% 0%; }
            }
            
            /* Apply Krishna colors to headings */
            h1, h2, h3, h4, h5, h6 {
              background: linear-gradient(45deg, #1E3A8A, #3B82F6, #8B5CF6) !important;
              -webkit-background-clip: text !important;
              -webkit-text-fill-color: transparent !important;
              background-clip: text !important;
            }
            
            /* Exclude specific elements from gradient effects */
            .hero-title span:not(.text-gradient),
            .hero-title .text-gray-600,
            .hero-title .text-gray-400,
            .hero-title .dark\\:text-gray-600,
            .hero-title .dark\\:text-gray-400 {
              -webkit-text-fill-color: initial !important;
              background: none !important;
              color: inherit !important;
            }
            
            /* Apply to navigation links */
            nav a, .nav-link {
              color: #3B82F6 !important;
              transition: color 0.3s ease !important;
            }
            
            nav a:hover, .nav-link:hover {
              color: #8B5CF6 !important;
            }
            
            .special-day-button {
              background: linear-gradient(45deg, #1E3A8A, #3B82F6, #8B5CF6, #1E3A8A);
              background-size: 200% 200%;
              animation: krishnaWave 5s ease-in-out infinite;
            }
            
            .special-day-text {
              background: linear-gradient(45deg, #1E3A8A, #3B82F6, #8B5CF6);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-clip: text;
            }
            
            .special-day-border {
              border: 2px solid;
              border-image: linear-gradient(45deg, #1E3A8A, #3B82F6, #8B5CF6) 1;
            }
            
            /* Scrolling Krishna gradient animation for all cards */
            .card, .service-card, .blog-card, .product-card, .project-card, .contact-card {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            /* More specific selectors for better targeting */
            div[class*="card"], div[class*="Card"] {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            .card::before, .service-card::before, .blog-card::before, .product-card::before, .project-card::before, .contact-card::before,
            div[class*="card"]::before, div[class*="Card"]::before {
              content: '' !important;
              position: absolute !important;
              top: -8px !important;
              left: -8px !important;
              right: -8px !important;
              bottom: -8px !important;
              background: linear-gradient(90deg, 
                #1E3A8A 0%, 
                #3B82F6 25%, 
                #8B5CF6 50%, 
                #3B82F6 75%, 
                #1E3A8A 100%
              ) !important;
              background-size: 200% 100% !important;
              animation: krishnaScroll 2s linear infinite !important;
              z-index: 1 !important;
              pointer-events: none !important;
              border-radius: 16px !important;
            }
            
            .card::after, .service-card::after, .blog-card::after, .product-card::after, .project-card::after, .contact-card::after,
            div[class*="card"]::after, div[class*="Card"]::after {
              content: '' !important;
              position: absolute !important;
              top: 6px !important;
              left: 6px !important;
              right: 6px !important;
              bottom: 6px !important;
              background: inherit !important;
              border-radius: inherit !important;
              z-index: 2 !important;
            }
            
            @keyframes krishnaScroll {
              0% { background-position: 0% 50%; }
              100% { background-position: 200% 50%; }
            }
            
            /* Ensure card content stays above the animation */
            .card > *, .service-card > *, .blog-card > *, .product-card > *, .project-card > *, .contact-card > *,
            div[class*="card"] > *, div[class*="Card"] > * {
              position: relative !important;
              z-index: 3 !important;
            }
          `}
        </style>
      )}

      {/* Scroll Effects */}
      {animationSettings?.scroll_effects && (
        <style>
          {`
            /* Apply scroll effects to cards and sections */
            .card, .section, .service-card, .product-card {
              transition: all 0.3s ease !important;
            }
            
            .card:hover, .section:hover, .service-card:hover, .product-card:hover {
              transform: scale(1.02) !important;
              box-shadow: 0 10px 25px rgba(255, 153, 51, 0.3) !important;
            }
            
            .special-day-scroll {
              transition: all 0.3s ease;
            }
            
            .special-day-scroll:hover {
              transform: scale(1.05);
              box-shadow: 0 10px 25px rgba(255, 153, 51, 0.3);
            }
          `}
        </style>
      )}

      {/* Scroll Effects for Krishnashtami */}
      {animationSettings?.scroll_effects && backgroundEffects?.krishna_colors && (
        <style>
          {`
            /* Apply Krishna-themed scroll effects to cards and sections */
            .card:hover, .section:hover, .service-card:hover, .product-card:hover {
              box-shadow: 0 10px 25px rgba(59, 130, 246, 0.4) !important;
            }
            
            .special-day-scroll:hover {
              box-shadow: 0 10px 25px rgba(59, 130, 246, 0.4);
            }
          `}
        </style>
      )}

      {/* Lotus Effects for Vinayaka Chavithi */}
      {backgroundEffects?.lotus_effects && (
        <style>
          {`
            /* Apply lotus petal effects to cards and sections */
            .card, .section, .service-card, .product-card {
              position: relative !important;
              overflow: hidden !important;
            }
            
            .card::before, .section::before, .service-card::before, .product-card::before {
              content: '' !important;
              position: absolute !important;
              top: -50% !important;
              left: -50% !important;
              width: 200% !important;
              height: 200% !important;
              background: radial-gradient(circle, rgba(255, 107, 53, 0.1) 0%, transparent 70%) !important;
              animation: lotusRotate 8s linear infinite !important;
              pointer-events: none !important;
            }
            
            @keyframes lotusRotate {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            .card:hover::before, .section:hover::before, .service-card:hover::before, .product-card:hover::before {
              animation-duration: 4s !important;
            }
            
            .special-day-lotus {
              position: relative;
              overflow: hidden;
            }
            
            .special-day-lotus::before {
              content: '';
              position: absolute;
              top: -50%;
              left: -50%;
              width: 200%;
              height: 200%;
              background: radial-gradient(circle, rgba(255, 107, 53, 0.1) 0%, transparent 70%);
              animation: lotusRotate 8s linear infinite;
              pointer-events: none;
            }
          `}
        </style>
      )}

      {/* Blessing Effects for Vinayaka Chavithi */}
      {animationSettings?.blessing_effects && (
        <style>
          {`
            /* Apply blessing glow effects to important elements */
            .card, .section, .service-card, .product-card {
              box-shadow: 0 4px 15px rgba(255, 215, 0, 0.2) !important;
              transition: all 0.3s ease !important;
            }
            
            .card:hover, .section:hover, .service-card:hover, .product-card:hover {
              box-shadow: 0 8px 25px rgba(255, 215, 0, 0.4) !important;
              transform: translateY(-2px) !important;
            }
            
            /* Add blessing particles to buttons */
            button:not(.admin-button):not(.dashboard-button)::after {
              content: '✨' !important;
              position: absolute !important;
              top: -10px !important;
              right: -10px !important;
              font-size: 12px !important;
              animation: blessingFloat 2s ease-in-out infinite !important;
              pointer-events: none !important;
              z-index: 5 !important;
            }
            
            @keyframes blessingFloat {
              0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.7; }
              50% { transform: translateY(-10px) rotate(180deg); opacity: 1; }
            }
            
            .special-day-blessing {
              box-shadow: 0 4px 15px rgba(255, 215, 0, 0.2);
              transition: all 0.3s ease;
            }
            
            .special-day-blessing:hover {
              box-shadow: 0 8px 25px rgba(255, 215, 0, 0.4);
              transform: translateY(-2px);
            }
          `}
        </style>
      )}

      {/* Flute Effects for Krishnashtami */}
      {backgroundEffects?.flute_effects && (
        <style>
          {`
            /* Apply flute melody effects to cards and sections */
            .card, .section, .service-card, .product-card {
              position: relative !important;
              overflow: hidden !important;
            }
            
            .card::before, .section::before, .service-card::before, .product-card::before {
              content: '🎵' !important;
              position: absolute !important;
              top: -20px !important;
              right: -20px !important;
              font-size: 24px !important;
              animation: fluteMelody 3s ease-in-out infinite !important;
              pointer-events: none !important;
              opacity: 0.6 !important;
            }
            
            @keyframes fluteMelody {
              0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
              25% { transform: translateY(-15px) rotate(5deg); opacity: 0.8; }
              50% { transform: translateY(-25px) rotate(0deg); opacity: 1; }
              75% { transform: translateY(-15px) rotate(-5deg); opacity: 0.8; }
            }
            
            .card:hover::before, .section:hover::before, .service-card:hover::before, .product-card:hover::before {
              animation-duration: 1.5s !important;
              opacity: 1 !important;
            }
            
            .special-day-flute {
              position: relative;
              overflow: hidden;
            }
            
            .special-day-flute::before {
              content: '🎵';
              position: absolute;
              top: -20px;
              right: -20px;
              font-size: 24px;
              animation: fluteMelody 3s ease-in-out infinite;
              pointer-events: none;
              opacity: 0.6;
            }
          `}
        </style>
      )}

      {/* Peacock Animations for Krishnashtami */}
      {backgroundEffects?.peacock_animations && (
        <style>
          {`
            /* Apply peacock feather effects to important elements */
            .card, .section, .service-card, .product-card {
              border: 2px solid transparent !important;
              background-clip: padding-box !important;
            }
            
            .card::after, .section::after, .service-card::after, .product-card::after {
              content: '' !important;
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              border: 2px solid transparent !important;
              border-radius: inherit !important;
              background: linear-gradient(45deg, #1E3A8A, #3B82F6, #8B5CF6, #06B6D4, #1E3A8A) !important;
              background-size: 400% 400% !important;
              animation: peacockFeather 6s ease-in-out infinite !important;
              mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0) !important;
              mask-composite: exclude !important;
              -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0) !important;
              -webkit-mask-composite: xor !important;
              pointer-events: none !important;
            }
            
            @keyframes peacockFeather {
              0%, 100% { background-position: 0% 50%; }
              25% { background-position: 100% 50%; }
              50% { background-position: 100% 100%; }
              75% { background-position: 0% 100%; }
            }
            
            .special-day-peacock {
              border: 2px solid transparent;
              background-clip: padding-box;
            }
            
            .special-day-peacock::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              border: 2px solid transparent;
              border-radius: inherit;
              background: linear-gradient(45deg, #1E3A8A, #3B82F6, #8B5CF6, #06B6D4, #1E3A8A);
              background-size: 400% 400%;
              animation: peacockFeather 6s ease-in-out infinite;
              mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
              mask-composite: exclude;
              -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
              -webkit-mask-composite: xor;
              pointer-events: none;
            }
          `}
        </style>
      )}

      {/* Moonlight Effects for Krishnashtami */}
      {animationSettings?.moonlight_effects && (
        <style>
          {`
            /* Apply moonlight glow effects to important elements */
            .card, .section, .service-card, .product-card {
              box-shadow: 0 4px 15px rgba(59, 130, 246, 0.2) !important;
              transition: all 0.3s ease !important;
            }
            
            .card:hover, .section:hover, .service-card:hover, .product-card:hover {
              box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4) !important;
              transform: translateY(-2px) !important;
            }
            
            /* Add moonlight particles to buttons using data attribute to avoid conflicts */
            button:not(.admin-button):not(.dashboard-button) {
              position: relative !important;
            }
            
            button:not(.admin-button):not(.dashboard-button)::before {
              content: '🌙' !important;
              position: absolute !important;
              top: -10px !important;
              right: -10px !important;
              font-size: 12px !important;
              animation: moonlightFloat 3s ease-in-out infinite !important;
              pointer-events: none !important;
              z-index: 10 !important;
            }
            
            @keyframes moonlightFloat {
              0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.7; }
              50% { transform: translateY(-15px) rotate(180deg); opacity: 1; }
            }
            
            .special-day-moonlight {
              box-shadow: 0 4px 15px rgba(59, 130, 246, 0.2);
              transition: all 0.3s ease;
            }
            
            .special-day-moonlight:hover {
              box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
              transform: translateY(-2px);
            }
          `}
        </style>
      )}

      {/* Divine Glow for Krishnashtami */}
      {animationSettings?.divine_glow && (
        <style>
          {`
            /* Apply divine glow effects to important elements */
            .card, .section, .service-card, .product-card {
              position: relative !important;
            }
            
            .card::before, .section::before, .service-card::before, .product-card::before {
              content: '' !important;
              position: absolute !important;
              top: -2px !important;
              left: -2px !important;
              right: -2px !important;
              bottom: -2px !important;
              background: linear-gradient(45deg, #1E3A8A, #3B82F6, #8B5CF6, #06B6D4) !important;
              border-radius: inherit !important;
              z-index: -1 !important;
              opacity: 0.3 !important;
              animation: divineGlow 4s ease-in-out infinite !important;
            }
            
            @keyframes divineGlow {
              0%, 100% { opacity: 0.3; transform: scale(1); }
              50% { opacity: 0.6; transform: scale(1.02); }
            }
            
            .card:hover::before, .section:hover::before, .service-card:hover::before, .product-card:hover::before {
              opacity: 0.8 !important;
              animation-duration: 2s !important;
            }
            
            .special-day-divine-glow {
              position: relative;
            }
            
            .special-day-divine-glow::before {
              content: '';
              position: absolute;
              top: -2px;
              left: -2px;
              right: -2px;
              bottom: -2px;
              background: linear-gradient(45deg, #1E3A8A, #3B82F6, #8B5CF6, #06B6D4);
              border-radius: inherit;
              z-index: -1;
              opacity: 0.3;
              animation: divineGlow 4s ease-in-out infinite;
            }
          `}
        </style>
      )}

      {/* Victory Colors for Dussehra */}
      {backgroundEffects?.victory_colors && (
        <style>
          {`
            /* Apply victory colors to buttons throughout the website */
            button:not(.admin-button):not(.dashboard-button) {
              background: linear-gradient(45deg, #DC2626, #F59E0B, #DC2626) !important;
              background-size: 200% 200% !important;
              animation: victoryWave 3s ease-in-out infinite !important;
              color: #fff !important;
              border: none !important;
            }
            
            /* Apply to primary buttons and CTAs */
            .btn-primary, .cta-button, .primary-button {
              background: linear-gradient(45deg, #DC2626, #F59E0B, #DC2626) !important;
              background-size: 200% 200% !important;
              animation: victoryWave 3s ease-in-out infinite !important;
            }
            
            @keyframes victoryWave {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
            
            /* Apply victory colors to headings */
            h1, h2, h3, h4, h5, h6 {
              background: linear-gradient(45deg, #DC2626, #F59E0B) !important;
              -webkit-background-clip: text !important;
              -webkit-text-fill-color: transparent !important;
              background-clip: text !important;
            }
            
            /* Exclude specific elements from gradient effects */
            .hero-title span:not(.text-gradient),
            .hero-title .text-gray-600,
            .hero-title .text-gray-400,
            .hero-title .dark\\:text-gray-600,
            .hero-title .dark\\:text-gray-400 {
              -webkit-text-fill-color: initial !important;
              background: none !important;
              color: inherit !important;
            }
            
            /* Apply to navigation links */
            nav a, .nav-link {
              color: #DC2626 !important;
              transition: color 0.3s ease !important;
            }
            
            nav a:hover, .nav-link:hover {
              color: #F59E0B !important;
            }
            
            /* Scrolling victory gradient animation for all cards */
            .card, .service-card, .blog-card, .product-card, .project-card, .contact-card {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            /* More specific selectors for better targeting */
            div[class*="card"], div[class*="Card"] {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            .card::before, .service-card::before, .blog-card::before, .product-card::before, .project-card::before, .contact-card::before,
            div[class*="card"]::before, div[class*="Card"]::before {
              content: '' !important;
              position: absolute !important;
              top: -8px !important;
              left: -8px !important;
              right: -8px !important;
              bottom: -8px !important;
              background: linear-gradient(90deg, 
                #DC2626 0%, 
                #F59E0B 25%, 
                #DC2626 50%, 
                #F59E0B 75%, 
                #DC2626 100%
              ) !important;
              background-size: 200% 100% !important;
              animation: victoryScroll 2s linear infinite !important;
              z-index: 1 !important;
              pointer-events: none !important;
              border-radius: 16px !important;
            }
            
            .card::after, .service-card::after, .blog-card::after, .product-card::after, .project-card::after, .contact-card::after,
            div[class*="card"]::after, div[class*="Card"]::after {
              content: '' !important;
              position: absolute !important;
              top: 6px !important;
              left: 6px !important;
              right: 6px !important;
              bottom: 6px !important;
              background: inherit !important;
              border-radius: inherit !important;
              z-index: 2 !important;
            }
            
            @keyframes victoryScroll {
              0% { background-position: 0% 50%; }
              100% { background-position: 200% 50%; }
            }
            
            /* Ensure card content stays above the animation */
            .card > *, .service-card > *, .blog-card > *, .product-card > *, .project-card > *, .contact-card > *,
            div[class*="card"] > *, div[class*="Card"] > * {
              position: relative !important;
              z-index: 3 !important;
            }
          `}
        </style>
      )}

      {/* Diwali Colors */}
      {backgroundEffects?.diwali_colors && (
        <style>
          {`
            /* Apply Diwali colors to buttons throughout the website */
            button:not(.admin-button):not(.dashboard-button) {
              background: linear-gradient(45deg, #F59E0B, #FCD34D, #F59E0B) !important;
              background-size: 200% 200% !important;
              animation: diwaliWave 4s ease-in-out infinite !important;
              color: #000 !important;
              border: none !important;
            }
            
            /* Apply to primary buttons and CTAs */
            .btn-primary, .cta-button, .primary-button {
              background: linear-gradient(45deg, #F59E0B, #FCD34D, #F59E0B) !important;
              background-size: 200% 200% !important;
              animation: diwaliWave 4s ease-in-out infinite !important;
            }
            
            @keyframes diwaliWave {
              0%, 100% { background-position: 0% 50%; }
              25% { background-position: 100% 50%; }
              50% { background-position: 100% 100%; }
              75% { background-position: 0% 100%; }
            }
            
            /* Apply Diwali colors to headings */
            h1, h2, h3, h4, h5, h6 {
              background: linear-gradient(45deg, #F59E0B, #FCD34D) !important;
              -webkit-background-clip: text !important;
              -webkit-text-fill-color: transparent !important;
              background-clip: text !important;
            }
            
            /* Exclude specific elements from gradient effects */
            .hero-title span:not(.text-gradient),
            .hero-title .text-gray-600,
            .hero-title .text-gray-400,
            .hero-title .dark\\:text-gray-600,
            .hero-title .dark\\:text-gray-400 {
              -webkit-text-fill-color: initial !important;
              background: none !important;
              color: inherit !important;
            }
            
            /* Apply to navigation links */
            nav a, .nav-link {
              color: #F59E0B !important;
              transition: color 0.3s ease !important;
            }
            
            nav a:hover, .nav-link:hover {
              color: #FCD34D !important;
            }
            
            /* Scrolling Diwali gradient animation for all cards */
            .card, .service-card, .blog-card, .product-card, .project-card, .contact-card {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            /* More specific selectors for better targeting */
            div[class*="card"], div[class*="Card"] {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            .card::before, .service-card::before, .blog-card::before, .product-card::before, .project-card::before, .contact-card::before,
            div[class*="card"]::before, div[class*="Card"]::before {
              content: '' !important;
              position: absolute !important;
              top: -8px !important;
              left: -8px !important;
              right: -8px !important;
              bottom: -8px !important;
              background: linear-gradient(90deg, 
                #F59E0B 0%, 
                #FCD34D 25%, 
                #F59E0B 50%, 
                #FCD34D 75%, 
                #F59E0B 100%
              ) !important;
              background-size: 200% 100% !important;
              animation: diwaliScroll 2s linear infinite !important;
              z-index: 1 !important;
              pointer-events: none !important;
              border-radius: 16px !important;
            }
            
            .card::after, .service-card::after, .blog-card::after, .product-card::after, .project-card::after, .contact-card::after,
            div[class*="card"]::after, div[class*="Card"]::after {
              content: '' !important;
              position: absolute !important;
              top: 6px !important;
              left: 6px !important;
              right: 6px !important;
              bottom: 6px !important;
              background: inherit !important;
              border-radius: inherit !important;
              z-index: 2 !important;
            }
            
            @keyframes diwaliScroll {
              0% { background-position: 0% 50%; }
              100% { background-position: 200% 50%; }
            }
            
            /* Ensure card content stays above the animation */
            .card > *, .service-card > *, .blog-card > *, .product-card > *, .project-card > *, .contact-card > *,
            div[class*="card"] > *, div[class*="Card"] > * {
              position: relative !important;
              z-index: 3 !important;
            }
          `}
        </style>
      )}

      {/* Christmas Colors */}
      {backgroundEffects?.christmas_colors && (
        <style>
          {`
            /* Apply Christmas colors to buttons throughout the website */
            button:not(.admin-button):not(.dashboard-button) {
              background: linear-gradient(45deg, #DC2626, #F59E0B, #FFFFFF, #3B82F6, #8B5CF6, #DC2626) !important;
              background-size: 200% 200% !important;
              animation: christmasWave 5s ease-in-out infinite !important;
              color: #fff !important;
              border: none !important;
            }
            
            /* Apply to primary buttons and CTAs */
            .btn-primary, .cta-button, .primary-button {
              background: linear-gradient(45deg, #DC2626, #F59E0B, #FFFFFF, #3B82F6, #8B5CF6, #DC2626) !important;
              background-size: 200% 200% !important;
              animation: christmasWave 5s ease-in-out infinite !important;
            }
            
            @keyframes christmasWave {
              0%, 100% { background-position: 0% 50%; }
              20% { background-position: 100% 50%; }
              40% { background-position: 100% 100%; }
              60% { background-position: 0% 100%; }
              80% { background-position: 0% 0%; }
            }
            
            /* Apply Christmas colors to headings */
            h1, h2, h3, h4, h5, h6 {
              background: linear-gradient(45deg, #DC2626, #F59E0B, #FFFFFF, #3B82F6, #8B5CF6) !important;
              -webkit-background-clip: text !important;
              -webkit-text-fill-color: transparent !important;
              background-clip: text !important;
            }
            
            /* Exclude specific elements from gradient effects */
            .hero-title span:not(.text-gradient),
            .hero-title .text-gray-600,
            .hero-title .text-gray-400,
            .hero-title .dark\\:text-gray-600,
            .hero-title .dark\\:text-gray-400 {
              -webkit-text-fill-color: initial !important;
              background: none !important;
              color: inherit !important;
            }
            
            /* Apply to navigation links */
            nav a, .nav-link {
              color: #DC2626 !important;
              transition: color 0.3s ease !important;
            }
            
            nav a:hover, .nav-link:hover {
              color: #3B82F6 !important;
            }
            
            /* Scrolling Christmas gradient animation for all cards */
            .card, .service-card, .blog-card, .product-card, .project-card, .contact-card {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            /* More specific selectors for better targeting */
            div[class*="card"], div[class*="Card"] {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            .card::before, .service-card::before, .blog-card::before, .product-card::before, .project-card::before, .contact-card::before,
            div[class*="card"]::before, div[class*="Card"]::before {
              content: '' !important;
              position: absolute !important;
              top: -8px !important;
              left: -8px !important;
              right: -8px !important;
              bottom: -8px !important;
              background: linear-gradient(90deg, 
                #DC2626 0%, 
                #F59E0B 20%, 
                #FFFFFF 40%, 
                #3B82F6 60%, 
                #8B5CF6 80%, 
                #DC2626 100%
              ) !important;
              background-size: 200% 100% !important;
              animation: christmasScroll 2s linear infinite !important;
              z-index: 1 !important;
              pointer-events: none !important;
              border-radius: 16px !important;
            }
            
            .card::after, .service-card::after, .blog-card::after, .product-card::after, .project-card::after, .contact-card::after,
            div[class*="card"]::after, div[class*="Card"]::after {
              content: '' !important;
              position: absolute !important;
              top: 6px !important;
              left: 6px !important;
              right: 6px !important;
              bottom: 6px !important;
              background: inherit !important;
              border-radius: inherit !important;
              z-index: 2 !important;
            }
            
            @keyframes christmasScroll {
              0% { background-position: 0% 50%; }
              100% { background-position: 200% 50%; }
            }
            
            /* Ensure card content stays above the animation */
            .card > *, .service-card > *, .blog-card > *, .product-card > *, .project-card > *, .contact-card > *,
            div[class*="card"] > *, div[class*="Card"] > * {
              position: relative !important;
              z-index: 3 !important;
            }
          `}
        </style>
      )}

      {/* Valentine Colors */}
      {backgroundEffects?.valentine_colors && (
        <style>
          {`
            /* Apply Valentine colors to buttons throughout the website */
            button:not(.admin-button):not(.dashboard-button) {
              background: linear-gradient(45deg, #EC4899, #F472B6, #EC4899) !important;
              background-size: 200% 200% !important;
              animation: valentineWave 3s ease-in-out infinite !important;
              color: #fff !important;
              border: none !important;
            }
            
            /* Apply to primary buttons and CTAs */
            .btn-primary, .cta-button, .primary-button {
              background: linear-gradient(45deg, #EC4899, #F472B6, #EC4899) !important;
              background-size: 200% 200% !important;
              animation: valentineWave 3s ease-in-out infinite !important;
            }
            
            @keyframes valentineWave {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
            
            /* Apply Valentine colors to headings */
            h1, h2, h3, h4, h5, h6 {
              background: linear-gradient(45deg, #EC4899, #F472B6) !important;
              -webkit-background-clip: text !important;
              -webkit-text-fill-color: transparent !important;
              background-clip: text !important;
            }
            
            /* Exclude specific elements from gradient effects */
            .hero-title span:not(.text-gradient),
            .hero-title .text-gray-600,
            .hero-title .text-gray-400,
            .hero-title .dark\\:text-gray-600,
            .hero-title .dark\\:text-gray-400 {
              -webkit-text-fill-color: initial !important;
              background: none !important;
              color: inherit !important;
            }
            
            /* Apply to navigation links */
            nav a, .nav-link {
              color: #EC4899 !important;
              transition: color 0.3s ease !important;
            }
            
            nav a:hover, .nav-link:hover {
              color: #F472B6 !important;
            }
            
            /* Scrolling Valentine gradient animation for all cards */
            .card, .service-card, .blog-card, .product-card, .project-card, .contact-card {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            /* More specific selectors for better targeting */
            div[class*="card"], div[class*="Card"] {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            .card::before, .service-card::before, .blog-card::before, .product-card::before, .project-card::before, .contact-card::before,
            div[class*="card"]::before, div[class*="Card"]::before {
              content: '' !important;
              position: absolute !important;
              top: -8px !important;
              left: -8px !important;
              right: -8px !important;
              bottom: -8px !important;
              background: linear-gradient(90deg, 
                #EC4899 0%, 
                #F472B6 25%, 
                #EC4899 50%, 
                #F472B6 75%, 
                #EC4899 100%
              ) !important;
              background-size: 200% 100% !important;
              animation: valentineScroll 2s linear infinite !important;
              z-index: 1 !important;
              pointer-events: none !important;
              border-radius: 16px !important;
            }
            
            .card::after, .service-card::after, .blog-card::after, .product-card::after, .project-card::after, .contact-card::after,
            div[class*="card"]::after, div[class*="Card"]::after {
              content: '' !important;
              position: absolute !important;
              top: 6px !important;
              left: 6px !important;
              right: 6px !important;
              bottom: 6px !important;
              background: inherit !important;
              border-radius: inherit !important;
              z-index: 2 !important;
            }
            
            @keyframes valentineScroll {
              0% { background-position: 0% 50%; }
              100% { background-position: 200% 50%; }
            }
            
            /* Ensure card content stays above the animation */
            .card > *, .service-card > *, .blog-card > *, .product-card > *, .project-card > *, .contact-card > *,
            div[class*="card"] > *, div[class*="Card"] > * {
              position: relative !important;
              z-index: 3 !important;
            }
          `}
        </style>
      )}

      {/* Holi Colors */}
      {backgroundEffects?.holi_colors && (
        <style>
          {`
            /* Apply Holi colors to buttons throughout the website */
            button:not(.admin-button):not(.dashboard-button) {
              background: linear-gradient(45deg, #EC4899, #F59E0B, #10B981, #3B82F6, #EC4899) !important;
              background-size: 300% 300% !important;
              animation: holiWave 6s ease-in-out infinite !important;
              color: #fff !important;
              border: none !important;
            }
            
            /* Apply to primary buttons and CTAs */
            .btn-primary, .cta-button, .primary-button {
              background: linear-gradient(45deg, #EC4899, #F59E0B, #10B981, #3B82F6, #EC4899) !important;
              background-size: 300% 300% !important;
              animation: holiWave 6s ease-in-out infinite !important;
            }
            
            @keyframes holiWave {
              0%, 100% { background-position: 0% 50%; }
              20% { background-position: 100% 50%; }
              40% { background-position: 100% 100%; }
              60% { background-position: 0% 100%; }
              80% { background-position: 0% 0%; }
            }
            
            /* Apply Holi colors to headings */
            h1, h2, h3, h4, h5, h6 {
              background: linear-gradient(45deg, #EC4899, #F59E0B, #10B981, #3B82F6) !important;
              -webkit-background-clip: text !important;
              -webkit-text-fill-color: transparent !important;
              background-clip: text !important;
            }
            
            /* Exclude specific elements from gradient effects */
            .hero-title span:not(.text-gradient),
            .hero-title .text-gray-600,
            .hero-title .text-gray-400,
            .hero-title .dark\\:text-gray-600,
            .hero-title .dark\\:text-gray-400 {
              -webkit-text-fill-color: initial !important;
              background: none !important;
              color: inherit !important;
            }
            
            /* Apply to navigation links */
            nav a, .nav-link {
              color: #EC4899 !important;
              transition: color 0.3s ease !important;
            }
            
            nav a:hover, .nav-link:hover {
              color: #10B981 !important;
            }
            
            /* Scrolling Holi gradient animation for all cards */
            .card, .service-card, .blog-card, .product-card, .project-card, .contact-card {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            /* More specific selectors for better targeting */
            div[class*="card"], div[class*="Card"] {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            .card::before, .service-card::before, .blog-card::before, .product-card::before, .project-card::before, .contact-card::before,
            div[class*="card"]::before, div[class*="Card"]::before {
              content: '' !important;
              position: absolute !important;
              top: -8px !important;
              left: -8px !important;
              right: -8px !important;
              bottom: -8px !important;
              background: linear-gradient(90deg, 
                #EC4899 0%, 
                #F59E0B 20%, 
                #10B981 40%, 
                #3B82F6 60%, 
                #EC4899 80%, 
                #F59E0B 100%
              ) !important;
              background-size: 200% 100% !important;
              animation: holiScroll 2s linear infinite !important;
              z-index: 1 !important;
              pointer-events: none !important;
              border-radius: 16px !important;
            }
            
            .card::after, .service-card::after, .blog-card::after, .product-card::after, .project-card::after, .contact-card::after,
            div[class*="card"]::after, div[class*="Card"]::after {
              content: '' !important;
              position: absolute !important;
              top: 6px !important;
              left: 6px !important;
              right: 6px !important;
              bottom: 6px !important;
              background: inherit !important;
              border-radius: inherit !important;
              z-index: 2 !important;
            }
            
            @keyframes holiScroll {
              0% { background-position: 0% 50%; }
              100% { background-position: 200% 50%; }
            }
            
            /* Ensure card content stays above the animation */
            .card > *, .service-card > *, .blog-card > *, .product-card > *, .project-card > *, .contact-card > *,
            div[class*="card"] > *, div[class*="Card"] > * {
              position: relative !important;
              z-index: 3 !important;
            }
          `}
        </style>
      )}

      {/* Sankranthi Colors */}
      {backgroundEffects?.sankranthi_colors && (
        <style>
          {`
            /* Apply Sankranthi colors to buttons throughout the website */
            button:not(.admin-button):not(.dashboard-button) {
              background: linear-gradient(45deg, #F59E0B, #FCD34D, #10B981, #F59E0B) !important;
              background-size: 200% 200% !important;
              animation: sankranthiWave 4s ease-in-out infinite !important;
              color: #000 !important;
              border: none !important;
            }
            
            /* Apply to primary buttons and CTAs */
            .btn-primary, .cta-button, .primary-button {
              background: linear-gradient(45deg, #F59E0B, #FCD34D, #10B981, #F59E0B) !important;
              background-size: 200% 200% !important;
              animation: sankranthiWave 4s ease-in-out infinite !important;
            }
            
            @keyframes sankranthiWave {
              0%, 100% { background-position: 0% 50%; }
              25% { background-position: 100% 50%; }
              50% { background-position: 100% 100%; }
              75% { background-position: 0% 100%; }
            }
            
            /* Apply Sankranthi colors to headings */
            h1, h2, h3, h4, h5, h6 {
              background: linear-gradient(45deg, #F59E0B, #FCD34D, #10B981) !important;
              -webkit-background-clip: text !important;
              -webkit-text-fill-color: transparent !important;
              background-clip: text !important;
            }
            
            /* Exclude specific elements from gradient effects */
            .hero-title span:not(.text-gradient),
            .hero-title .text-gray-600,
            .hero-title .text-gray-400,
            .hero-title .dark\\:text-gray-600,
            .hero-title .dark\\:text-gray-400 {
              -webkit-text-fill-color: initial !important;
              background: none !important;
              color: inherit !important;
            }
            
            /* Apply to navigation links */
            nav a, .nav-link {
              color: #F59E0B !important;
              transition: color 0.3s ease !important;
            }
            
            nav a:hover, .nav-link:hover {
              color: #10B981 !important;
            }
            
            /* Scrolling Sankranthi gradient animation for all cards */
            .card, .service-card, .blog-card, .product-card, .project-card, .contact-card {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            /* More specific selectors for better targeting */
            div[class*="card"], div[class*="Card"] {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            .card::before, .service-card::before, .blog-card::before, .product-card::before, .project-card::before, .contact-card::before,
            div[class*="card"]::before, div[class*="Card"]::before {
              content: '' !important;
              position: absolute !important;
              top: -8px !important;
              left: -8px !important;
              right: -8px !important;
              bottom: -8px !important;
              background: linear-gradient(90deg, 
                #F59E0B 0%, 
                #FCD34D 25%, 
                #10B981 50%, 
                #FCD34D 75%, 
                #F59E0B 100%
              ) !important;
              background-size: 200% 100% !important;
              animation: sankranthiScroll 2s linear infinite !important;
              z-index: 1 !important;
              pointer-events: none !important;
              border-radius: 16px !important;
            }
            
            .card::after, .service-card::after, .blog-card::after, .product-card::after, .project-card::after, .contact-card::after,
            div[class*="card"]::after, div[class*="Card"]::after {
              content: '' !important;
              position: absolute !important;
              top: 6px !important;
              left: 6px !important;
              right: 6px !important;
              bottom: 6px !important;
              background: inherit !important;
              border-radius: inherit !important;
              z-index: 2 !important;
            }
            
            @keyframes sankranthiScroll {
              0% { background-position: 0% 50%; }
              100% { background-position: 200% 50%; }
            }
            
            /* Ensure card content stays above the animation */
            .card > *, .service-card > *, .blog-card > *, .product-card > *, .project-card > *, .contact-card > *,
            div[class*="card"] > *, div[class*="Card"] > * {
              position: relative !important;
              z-index: 3 !important;
            }
          `}
        </style>
      )}

      {/* New Year Colors */}
      {backgroundEffects?.newyear_colors && (
        <style>
          {`
            /* Apply New Year colors to buttons throughout the website */
            button:not(.admin-button):not(.dashboard-button) {
              background: linear-gradient(45deg, #1E40AF, #3B82F6, #8B5CF6, #EC4899, #F59E0B, #1E40AF) !important;
              background-size: 300% 300% !important;
              animation: newYearWave 6s ease-in-out infinite !important;
              color: #fff !important;
              border: none !important;
            }
            
            /* Apply to primary buttons and CTAs */
            .btn-primary, .cta-button, .primary-button {
              background: linear-gradient(45deg, #1E40AF, #3B82F6, #8B5CF6, #EC4899, #F59E0B, #1E40AF) !important;
              background-size: 300% 300% !important;
              animation: newYearWave 6s ease-in-out infinite !important;
            }
            
            @keyframes newYearWave {
              0%, 100% { background-position: 0% 50%; }
              16.66% { background-position: 100% 50%; }
              33.33% { background-position: 100% 100%; }
              50% { background-position: 0% 100%; }
              66.66% { background-position: 0% 0%; }
              83.33% { background-position: 100% 0%; }
            }
            
            /* Apply New Year colors to headings */
            h1, h2, h3, h4, h5, h6 {
              background: linear-gradient(45deg, #1E40AF, #3B82F6, #8B5CF6, #EC4899, #F59E0B) !important;
              -webkit-background-clip: text !important;
              -webkit-text-fill-color: transparent !important;
              background-clip: text !important;
            }
            
            /* Exclude specific elements from gradient effects */
            .hero-title span:not(.text-gradient),
            .hero-title .text-gray-600,
            .hero-title .text-gray-400,
            .hero-title .dark\\:text-gray-600,
            .hero-title .dark\\:text-gray-400 {
              -webkit-text-fill-color: initial !important;
              background: none !important;
              color: inherit !important;
            }
            
            /* Apply to navigation links */
            nav a, .nav-link {
              color: #3B82F6 !important;
              transition: color 0.3s ease !important;
            }
            
            nav a:hover, .nav-link:hover {
              color: #EC4899 !important;
            }
            
            /* Scrolling New Year gradient animation for all cards */
            .card, .service-card, .blog-card, .product-card, .project-card, .contact-card {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            /* More specific selectors for better targeting */
            div[class*="card"], div[class*="Card"] {
              position: relative !important;
              border-radius: 8px !important;
              overflow: hidden !important;
            }
            
            .card::before, .service-card::before, .blog-card::before, .product-card::before, .project-card::before, .contact-card::before,
            div[class*="card"]::before, div[class*="Card"]::before {
              content: '' !important;
              position: absolute !important;
              top: -8px !important;
              left: -8px !important;
              right: -8px !important;
              bottom: -8px !important;
              background: linear-gradient(90deg, 
                #1E40AF 0%, 
                #3B82F6 20%, 
                #8B5CF6 40%, 
                #EC4899 60%, 
                #F59E0B 80%, 
                #1E40AF 100%
              ) !important;
              background-size: 200% 100% !important;
              animation: newYearScroll 2s linear infinite !important;
              z-index: 1 !important;
              pointer-events: none !important;
              border-radius: 16px !important;
            }
            
            .card::after, .service-card::after, .blog-card::after, .product-card::after, .project-card::after, .contact-card::after,
            div[class*="card"]::after, div[class*="Card"]::after {
              content: '' !important;
              position: absolute !important;
              top: 6px !important;
              left: 6px !important;
              right: 6px !important;
              bottom: 6px !important;
              background: inherit !important;
              border-radius: inherit !important;
              z-index: 2 !important;
            }
            
            @keyframes newYearScroll {
              0% { background-position: 0% 50%; }
              100% { background-position: 200% 50%; }
            }
            
            /* Ensure card content stays above the animation */
            .card > *, .service-card > *, .blog-card > *, .product-card > *, .project-card > *, .contact-card > *,
            div[class*="card"] > *, div[class*="Card"] > * {
              position: relative !important;
              z-index: 3 !important;
            }
            
            /* New Year celebration effects */
            .card, .section, .service-card, .product-card {
              position: relative !important;
            }
            
            .card::before, .section::before, .service-card::before, .product-card::before {
              content: '' !important;
              position: absolute !important;
              top: -2px !important;
              left: -2px !important;
              right: -2px !important;
              bottom: -2px !important;
              background: linear-gradient(45deg, #1E40AF, #3B82F6, #8B5CF6, #EC4899, #F59E0B) !important;
              border-radius: inherit !important;
              z-index: -1 !important;
              opacity: 0.3 !important;
              animation: newYearGlow 4s ease-in-out infinite !important;
            }
            
            @keyframes newYearGlow {
              0%, 100% { opacity: 0.3; transform: scale(1); }
              50% { opacity: 0.6; transform: scale(1.02); }
            }
            
            .card:hover::before, .section:hover::before, .service-card:hover::before, .product-card:hover::before {
              opacity: 0.8 !important;
              animation-duration: 2s !important;
            }
          `}
        </style>
      )}

      {/* Button Effects */}
      {animationSettings?.button_effects && (
        <style>
          {`
            /* Apply shimmer effect to all buttons */
            button:not(.admin-button):not(.dashboard-button) {
              position: relative !important;
              overflow: hidden !important;
            }
            
            button:not(.admin-button):not(.dashboard-button)::before {
              content: '' !important;
              position: absolute !important;
              top: 0 !important;
              left: -100% !important;
              width: 100% !important;
              height: 100% !important;
              background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent) !important;
              transition: left 0.5s !important;
              z-index: 1 !important;
            }
            
            button:not(.admin-button):not(.dashboard-button):hover::before {
              left: 100% !important;
            }
            
            .special-day-button-effect {
              position: relative;
              overflow: hidden;
            }
            
            .special-day-button-effect::before {
              content: '';
              position: absolute;
              top: 0;
              left: -100%;
              width: 100%;
              height: 100%;
              background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
              transition: left 0.5s;
              z-index: 1;
            }
            
            .special-day-button-effect:hover::before {
              left: 100%;
            }
          `}
        </style>
      )}

      {/* Banner Styling */}
      <style>
        {`
          /* Ensure the banner is sticky and properly positioned */
          .special-day-banner {
            position: sticky !important;
            top: 0 !important;
            z-index: 20;
            width: 100%;
            display: block;
            height: 40px;
          }
          
          /* Reset header position when no banner */
          body:not(.special-day-banner-visible) header {
            top: 0 !important;
          }
        `}
      </style>
    </div>
  );
};

export default SpecialDayEffects;
